import React from 'react';
import styled from 'styled-components';
import { UserController } from 'api';
import { AuthContextUser, ListingLabelContextType } from 'contexts';
import { isAuthForRole, isUserMlsStaff } from 'utils';

import {
  FormattedAgentType,
  FormattedAgentForAppointmentDeskType,
  SearchType,
  ColumnOptions,
  AppointmentColumnOptions,
} from './types';
import {
  StyledAddressName,
  StyledMlsName,
  StyledFlexWrapper,
} from './StyledComponents';
import { Contact } from '../Contact';
import { addAgentToHistory } from '../../api/RecentSearchHistoryController';
import AgentRowActions from './AgentRowActions';
import { SubscriptionTag } from './components';
import { MLS_TYPE_LABEL_MAP } from './constants';

export const mapAgentResultsToTable =
  (auth: AuthContextUser) => (agent: any, index: number) => {
    const enableChat =
      agent.orgHasChatFeature &&
      (!agent.isAgentChatHidden ||
        isAuthForRole({
          auth,
          org: agent.orgId,
          location: agent.location,
        }));
    return {
      key: index,
      id: agent.id,
      agent,
      email: agent.email,
      enableChat,
    };
  };

const getAgentSearchAppointmentDeskCrossOrg: (
  options: AppointmentColumnOptions,
  setActiveRow: React.Dispatch<React.SetStateAction<null | number>>,
  getBoardName: ListingLabelContextType['getBoardName'],
) => SearchType<FormattedAgentForAppointmentDeskType> = (
  options,
  setActiveRow,
  getBoardName,
) => ({
  columns: [
    {
      title: '',
      dataIndex: 'agent',
      key: 'agent',
      className: 'image-column cross-org',
      render: (agent: FormattedAgentForAppointmentDeskType['agent']) => {
        const contactUser = {
          id: agent.id,
          firstName: agent.firstName,
          lastName: agent.lastName,
          avatarFilestackId: agent.avatarFilestackId,
        };

        return (
          <Contact
            avatarSize={32}
            contact={contactUser}
            disableClick
            secondaryText={
              <StyledAddressName ellipsis block>
                {agent.addressName}
              </StyledAddressName>
            }
          />
        );
      },
    },
    {
      title: 'MLS Name',
      dataIndex: 'mlsTypes',
      key: 'mlsTypes',
      className: 'cross-org',
      width: 150,
      render: (mlsTypes: FormattedAgentForAppointmentDeskType['mlsTypes']) => {
        const mlsTypeText =
          Array.isArray(mlsTypes) &&
          mlsTypes
            .map(mlsType => MLS_TYPE_LABEL_MAP[mlsType] ?? mlsType)
            .join(', ');

        return (
          !!mlsTypeText && (
            <StyledFlexWrapper $justifyContent="flex-start">
              <StyledMlsName data-cy={`agent-mls-type-${mlsTypeText}`}>
                {mlsTypeText}
              </StyledMlsName>
            </StyledFlexWrapper>
          )
        );
      },
    },
    {
      title: 'Brokerage Name',
      dataIndex: 'mls_numbers',
      key: 'mls_numbers',
      className: 'ellipsis cross-org',
      ellipsis: true,
      width: 200,
      render: (
        mlsNumbers: FormattedAgentForAppointmentDeskType['mls_numbers'],
      ) =>
        [mlsNumbers].length > 0 && (
          <StyledMlsNumbersWrapper>
            {mlsNumbers?.map(mlsData => (
              <div
                key={mlsData.number}
                data-cy={`agent-mls-numbers-${getBoardName(mlsData.board)}-${
                  mlsData.number
                }`}
              >
                {getBoardName(mlsData.board, false)}: {mlsData.number}
              </div>
            ))}
          </StyledMlsNumbersWrapper>
        ),
    },
    {
      title: 'Subscription Tag',
      dataIndex: 'subscriptionTag',
      key: 'subscriptionTag',
      className: 'cross-org',
      render: (
        subscriptionTag: FormattedAgentForAppointmentDeskType['subscriptionTag'],
      ) => (
        <SubscriptionTag
          subscriptionTag={subscriptionTag}
          toolTipTextType="agent"
        />
      ),
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'button',
      className: 'cross-org',
      render: (id: string, record, index) => (
        <AgentRowActions
          id={id}
          index={index}
          subscriptionTag={record.subscriptionTag}
          coopType={record.agent.coopType}
          isMlsStaff={record.isUserMlsStaff}
          role={record.agent.role}
          enableChat
          isAppointmentDesk
        />
      ),
    },
  ],
  // @ts-expect-error: response type mismatch
  search: async (
    debouncedQuery,
    forceBlur,
    { auth, mlsOptionsCount, mlsFilter },
  ) => {
    let mlsTypes: string[] = [];
    if (mlsFilter && mlsFilter.length !== mlsOptionsCount) mlsTypes = mlsFilter;

    const { users: agents } = await UserController.searchForAnsweringService({
      name: debouncedQuery,
      page: 0,
      size: 10,
      ...(mlsTypes.length ? { mlsTypes } : {}),
    });

    return agents.map((agent, index) => ({
      key: index,
      id: agent.id,
      agent,
      email: agent.email,
      mls_numbers: agent.mls_numbers,
      subscriptionTag: agent.subscriptionTag,
      mlsTypes: agent.mlsTypes,
      isUserMlsStaff: isUserMlsStaff(auth),
    }));
  },
  onConfirm: (itemSelected, { openAgentDrawer }) => {
    // itemSelected is a Org
    const { id } = itemSelected;
    openAgentDrawer(id);
  },
  createOnRow:
    ({ openAgentDrawer }) =>
    (item, index) => ({
      onClick: () => openAgentDrawer(item.id, item.subscriptionTag),
      onMouseMove: () => setActiveRow(index),
      onMouseLeave: () => setActiveRow(null),
      'data-cy': `universal-agent-item-${item.id}`,
    }),
});

const StyledMlsNumbersWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const getAgentSearchCrossOrg: (
  options: ColumnOptions,
  setActiveRow: React.Dispatch<React.SetStateAction<null | number>>,
  getBoardName: ListingLabelContextType['getBoardName'],
) => SearchType<FormattedAgentType> = (
  options,
  setActiveRow,
  getBoardName,
) => ({
  columns: [
    {
      title: '',
      dataIndex: 'agent',
      key: 'agent',
      className: 'image-column cross-org',
      width: '25%',
      render: (agent: FormattedAgentType['agent']) => {
        const contactUser = {
          id: agent.id,
          firstName: agent.firstName,
          lastName: agent.lastName,
          avatarFilestackId: agent.avatarFilestackId,
        };

        return (
          <Contact
            avatarSize={32}
            contact={contactUser}
            underlineOnHover
            secondaryText={
              <StyledAddressName>{agent.addressName}</StyledAddressName>
            }
            data-cy="agent-name"
          />
        );
      },
    },
    {
      title: '',
      dataIndex: 'agent',
      key: 'mls_numbers',
      className: 'ellipsis cross-org',
      ellipsis: true,
      render: (agent: FormattedAgentType['agent']) => {
        if (agent.hideMemberId) return;
        const mlsNumbers = agent.mlsNumbers
          ? agent.mlsNumbers.map((mlsNumber: any) => (
              <StyledMLSNumber key={mlsNumber.number}>
                {getBoardName(mlsNumber.board, false)}: {mlsNumber.number}
              </StyledMLSNumber>
            ))
          : '';
        return <div data-cy="agent-mls-numbers">{mlsNumbers}</div>;
      },
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'button',
      className: 'crossOrg-button-column',
      width: 120,
      render: (id: string, { enableChat, agent }, index) => (
        <AgentRowActions
          id={id}
          index={index}
          role={agent.role}
          coopType={agent.coopType}
          enableChat={
            options.showChatBtn && enableChat && !agent.isChatDisabled
          }
        />
      ),
    },
  ],
  search: async (debouncedQuery, _forceBlur, { auth, brokerageFilter }) => {
    const { users: agents } = await UserController.searchCrossOrg({
      name: debouncedQuery,
      page: 0,
      isCrossOrgSearch: true,
      isLicensed: true,
      excludeInternalOrgs: false,
      size: 10,
      ...(brokerageFilter === 'myBrokerage' ? { orgId: auth.org.id } : {}),
    });

    return agents.map(mapAgentResultsToTable(auth));
  },
  onConfirm: (itemSelected, { openAgentDrawer }) => {
    // itemSelected is a Org
    const { id } = itemSelected;
    addAgentToHistory(id);
    openAgentDrawer(id);
  },
  createOnRow:
    ({ openAgentDrawer }) =>
    (item, index) => ({
      onClick: () => {
        addAgentToHistory(item.id);
        openAgentDrawer(item.id);
      },
      onMouseMove: () => setActiveRow(index),
      onMouseLeave: () => setActiveRow(null),
    }),
});

export { getAgentSearchCrossOrg, getAgentSearchAppointmentDeskCrossOrg };

const StyledMLSNumber = styled.p`
  margin: 0;
`;
